/**
 *
 * @module playReadyDrmProvider
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md#playready
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import DrmProvider from './drmProvider';
import DrmManager from './drmManager';

const Util = Services.Util;
const TokenManager = Token.TokenManager;
const DrmClientEndpoint = Services.Drm.DrmClientEndpoint;
const DrmType = Services.Media.DrmType;
const PlayReadyMessage = Services.Drm.PlayReadyMessage;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    instanceStrict
} = Check.assert;

/**
 *
 * @class PlayReadyDrmProvider
 * @since 3.2.0
 * @desc PlayReady is the Microsoft content encryption standard.
 *
 */
export default class PlayReadyDrmProvider extends DrmProvider {

    /**
     *
     * @param {SDK.Drm.DrmManager} provider
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(provider, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'PlayReadyDrmProvider(provider, tokenManager, logger)',
            get provider() {
                return `${this.ctor} needs valid provider SDK.Drm.DrmManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(provider, DrmManager, errorMessage.provider, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        super(provider, tokenManager, logger);

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.licenseRequestUri = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.playReadyLicense].href;
        }, '');

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.licenseRequestHeaders = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.playReadyLicense].headers;
        }, {});

        /**
         *
         * @access private
         * @type {String}
         * @desc DRM identifier String
         *
         */
        this.type = DrmType.PLAYREADY;

        this.logger.log(PlayReadyDrmProvider.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Object} [body={}]
     * @desc Gets a license required for decrypting PlayReady protected content.
     * @emits {MediaFailure} - Raised when the method fails, describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getPlayReadyLicense(body = {}) {

        const errorMessage = {
            method: 'PlayReadyDrmProvider.getPlayReadyLicense(body)',
            get body() {
                return `${this.method} body should be an Object`;
            }
        };

        object(body, errorMessage.body, InvalidArgumentException);

        const { provider, logger, licenseRequestHeaders, failureEvent } = this;
        const message = new PlayReadyMessage(body, licenseRequestHeaders);

        return provider.getPlayReadyLicense(message).then((data) => {

            return Promise.resolve(data);

        }).catch((exception) => {

            logger.error(PlayReadyDrmProvider.name, 'PlayReady license request failure.');
            logger.info(PlayReadyDrmProvider.name, `Dispatch ${failureEvent} event.`);

            this.emit(failureEvent, exception);

            return Promise.reject(exception);

        });

    }

}



// WEBPACK FOOTER //
// ./src/drm/playReadyDrmProvider.js